import { UIEvent, useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@twilio-paste/core/box";
import { Text } from "@twilio-paste/core/text";
import { Spinner } from "@twilio-paste/core/spinner";
import { Message } from "@twilio/conversations";
import throttle from "lodash.throttle";

import { MessageBubble } from "./MessageBubble";
import { HistoryMessageBubble, HistoryFeedMessage } from "./customized/HistoryMessageBubble";
import { AppState } from "../store/definitions";
import { getMoreMessages } from "../store/actions/genericActions";
import { getDaysOld } from "../utils/getDaysOld";
import { MessageListSeparator } from "./MessageListSeparator";
import { MESSAGES_SPINNER_BOX_HEIGHT } from "../constants";
import contactBackend from "../utils/contactBackend";
import {
    clearBackgroundMessageAlert,
    triggerBackgroundMessageAlert
} from "../utils/backgroundMessageAlert";
import { isSystemStartMessage } from "../utils/systemStartMessage";
import {
    conversationEventContainerStyles,
    conversationEventTitleStyles,
    conversationEventDateStyles,
    spinnerContainerStyles,
    messageListStyles,
    innerContainerStyles,
    outerContainerStyles,
    participantTypingStyles
} from "./styles/MessageList.styles";

const HISTORY_PAGE_SIZE = 30;

const sortHistoryAsc = (list: HistoryFeedMessage[]) =>
    [...list].sort((a, b) => {
        const ta = new Date(a.dateCreated || 0).getTime();
        const tb = new Date(b.dateCreated || 0).getTime();
        return ta - tb;
    });

const isLastOfUserGroup = (message: Message, i: number, messages: Message[]) => {
    const nextMessage = messages[i + 1];

    // if there's no message afterwards, it's definitely the last of a group
    if (!nextMessage) {
        return true;
    }

    // if the author of the next message is different from current one's, then yes, this message is last of its group
    return nextMessage.author !== message.author;
};

const isFirstOfDateGroup = (message: Message, i: number, messages: Message[]) => {
    const prevMessage = messages[i - 1];

    // if the previous message has a date older than the current message, this message is the first for this date
    return getDaysOld(prevMessage.dateCreated) > getDaysOld(message.dateCreated);
};

export const MessageList = () => {
    const conversationSid = useSelector((state: AppState) => state.session.conversationSid) || {};
    const { messages, participants, users, conversation, conversationsClient } = useSelector((state: AppState) => ({
        messages: state.chat.messages,
        participants: state.chat.participants,
        users: state.chat.users,
        conversation: state.chat.conversation,
        conversationsClient: state.chat.conversationsClient
    }));
    
    const preEngagementData = useSelector((state: AppState) => state.session.preEngagementData);
    const backgroundAlert = useSelector((state: AppState) => state.config.backgroundAlert);
    const visibleMessages = (messages || []).filter((message) => !isSystemStartMessage(message));
    
    const dispatch = useDispatch();
    const messageListRef = useRef<HTMLDivElement>(null);
    const isLoadingMessages = useRef(false);
    const oldMessagesLength = useRef((messages || []).length);
    const [hasLoadedAllMessages, setHasLoadedAllMessages] = useState(true);
    const [focusIndex, setFocusIndex] = useState(
        messages && messages.length ? messages[messages?.length - 1].index : -1
    );
    const [shouldFocusLatest, setShouldFocusLatest] = useState(false);
    const [isPageVisible, setIsPageVisible] = useState(true);
    const isPageVisibleRef = useRef(true);
    const backgroundAlertRef = useRef(backgroundAlert);

    backgroundAlertRef.current = backgroundAlert;
    isPageVisibleRef.current = isPageVisible;

    const email = preEngagementData?.Email;
    const [pastMessages, setPastMessages] = useState<HistoryFeedMessage[]>([]);
    const [hasMoreHistory, setHasMoreHistory] = useState(false);
    const [historyBefore, setHistoryBefore] = useState<string | undefined>();
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const historyLoadedForSid = useRef<string | null>(null);
    const isLoadingHistoryRef = useRef(false);
    const pendingScrollRestore = useRef<number | null>(null);

    const loadHistoryPage = useCallback(
        async (opts?: { before?: string; prepend?: boolean }) => {
            const emailValue = (email || "").toString().trim();
            const sid = typeof conversationSid === "string" ? conversationSid : "";
            if (!emailValue || isLoadingHistoryRef.current) {
                return;
            }

            isLoadingHistoryRef.current = true;
            setIsLoadingHistory(true);

            try {
                const resp = await contactBackend<{
                    messages?: HistoryFeedMessage[];
                    hasMore?: boolean;
                    nextBefore?: string;
                }>("/historyFeed", {
                    email: emailValue,
                    excludeConversationSid: sid || undefined,
                    before: opts?.before,
                    limit: HISTORY_PAGE_SIZE
                });

                const page = sortHistoryAsc(resp.messages || []);
                if (opts?.prepend && messageListRef.current) {
                    pendingScrollRestore.current = messageListRef.current.scrollHeight;
                }

                setPastMessages((prev) => {
                    if (!opts?.prepend) {
                        return page;
                    }
                    const existing = new Set(prev.map((m) => m.sid));
                    const older = page.filter((m) => !existing.has(m.sid));
                    return sortHistoryAsc([...older, ...prev]);
                });
                setHasMoreHistory(Boolean(resp.hasMore));
                const next = resp.nextBefore || page[0]?.dateCreated;
                setHistoryBefore(next ? new Date(next).toISOString() : undefined);
            } catch (error) {
                console.warn("加载历史消息失败", error);
                if (!opts?.prepend) {
                    setPastMessages([]);
                    setHasMoreHistory(false);
                }
            } finally {
                isLoadingHistoryRef.current = false;
                setIsLoadingHistory(false);
            }
        },
        [email, conversationSid]
    );

    // 进入新会话后，延后异步加载跨会话历史，不挡住聊天窗首屏
    useEffect(() => {
        const sid = typeof conversationSid === "string" ? conversationSid : "";
        const emailValue = (email || "").toString().trim();
        if (!emailValue || !sid) {
            return undefined;
        }
        if (historyLoadedForSid.current === sid) {
            return undefined;
        }
        historyLoadedForSid.current = sid;
        setPastMessages([]);
        setHasMoreHistory(false);
        setHistoryBefore(undefined);

        let idleId: number | undefined;
        const timer = window.setTimeout(() => {
            const run = () => {
                loadHistoryPage();
            };
            const ric = (window as Window & {
                requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
            }).requestIdleCallback;
            if (typeof ric === "function") {
                idleId = ric(run, { timeout: 2000 });
            } else {
                run();
            }
        }, 600);

        return () => {
            window.clearTimeout(timer);
            const cic = (window as Window & {
                cancelIdleCallback?: (id: number) => void;
            }).cancelIdleCallback;
            if (idleId !== undefined && typeof cic === "function") {
                cic(idleId);
            }
        };
    }, [email, conversationSid, loadHistoryPage]);

    useLayoutEffect(() => {
        if (pendingScrollRestore.current === null || !messageListRef.current) {
            return;
        }
        const el = messageListRef.current;
        const prevHeight = pendingScrollRestore.current;
        pendingScrollRestore.current = null;
        el.scrollTop -= el.scrollHeight - prevHeight;
    }, [pastMessages]);

    // 不再自动连拉多页铺满视口（会拖慢进聊天）；仅用户上滑时加载更早历史

    // 页面可见性（切 App/浮窗时尝试即时提醒；iOS 微信挂起 JS 则无法提醒）
    useEffect(() => {
        const updatePageVisibility = () => {
            const isVisible = !document.hidden;
            console.log("页面可见性更新:", {
                documentHidden: document.hidden,
                计算结果: isVisible
            });
            isPageVisibleRef.current = isVisible;
            setIsPageVisible(isVisible);
            if (isVisible) {
                clearBackgroundMessageAlert();
            }
        };

        const events = ["visibilitychange", "pageshow", "pagehide"];

        events.forEach((event) => {
            document.addEventListener(event, updatePageVisibility);
            window.addEventListener(event, updatePageVisibility);
        });

        updatePageVisibility();

        return () => {
            events.forEach((event) => {
                document.removeEventListener(event, updatePageVisibility);
                window.removeEventListener(event, updatePageVisibility);
            });
            clearBackgroundMessageAlert();
        };
    }, []);

    // 在 useEffect 之前添加初始检查
    useEffect(() => {
        console.log("MessageList 组件初始化，初始状态:", {
            页面可见性: isPageVisible,
            消息数量: messages?.length || 0,
            对话状态: conversation?.state,
            当前用户: conversationsClient?.user.identity,
            backgroundAlert
        });
    }, []);

    const [windowState, setWindowState] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
        isMaximized: window.innerWidth === screen.availWidth && window.innerHeight === screen.availHeight
    });

    useEffect(() => {
        const handleResize = () => {
            const newState = {
                width: window.innerWidth,
                height: window.innerHeight,
                isMaximized: window.innerWidth === screen.availWidth && window.innerHeight === screen.availHeight
            };
            console.log("窗口大小变化:", newState);
            setWindowState(newState);
        };

        window.addEventListener('resize', handleResize);
        
        // 初始检查
        handleResize();
        
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const updateFocus = (newFocus: number) => {
        if (newFocus < 0 || !messages || !messages.length || newFocus > messages[messages.length - 1].index) {
            return;
        }

        if (shouldFocusLatest) {
            setFocusIndex(messages[messages.length - 1].index);
            setShouldFocusLatest(false);
        } else {
            setFocusIndex(newFocus);
        }
    };

    const scrollToBottom = () => {
        if (!messageListRef.current) {
            return;
        }

        messageListRef.current.scrollTop = 0;
    };

    useEffect(() => {
        const isAgentAuthor = (author?: string | null) => {
            if (!author) {
                return false;
            }
            const customerId = conversationsClient?.user.identity;
            const sid = typeof conversationSid === "string" ? conversationSid : undefined;
            if (customerId && author === customerId) {
                return false;
            }
            if (author.startsWith("FX")) {
                return false;
            }
            if (author === "Concierge" || (sid && author === sid)) {
                return false;
            }
            return true;
        };

        const messageListener = (message: Message) => {
            console.log("messageListener", message);
            if (!document.activeElement?.hasAttribute("data-message-bubble")) {
                setShouldFocusLatest(true);
            }

            const belongsToCurrentUser = message.author === conversationsClient?.user.identity;
            const isAgentMessage = isAgentAuthor(message.author);

            if (belongsToCurrentUser) {
                scrollToBottom();
            }

            // 模板消息改由服务端 Webhook + 在线状态决定，避免人在聊天窗时误发
            // 页内声光震动仍可在后台（JS 存活）时触发
            const pageInBackground = document.hidden || !isPageVisibleRef.current;
            if (isAgentMessage && pageInBackground && backgroundAlertRef.current?.enabled) {
                const cfg = backgroundAlertRef.current;
                triggerBackgroundMessageAlert({
                    sound: cfg.sound !== false,
                    vibrate: cfg.vibrate !== false,
                    flash: cfg.flash !== false
                });
            }
        };

        conversation?.addListener("messageAdded", messageListener);

        return () => {
            conversation?.removeListener("messageAdded", messageListener);
        };
    }, [
        conversation,
        conversationsClient,
        conversationSid
    ]);

    useEffect(() => {
        const checkIfAllMessagesLoaded = async () => {
            const totalMessagesCount = await conversation?.getMessagesCount();
            if (totalMessagesCount) {
                setHasLoadedAllMessages(totalMessagesCount === messages?.length);
            }

            // if messages were added to state, loading is complete
            if (messages && oldMessagesLength.current < messages?.length) {
                isLoadingMessages.current = false;
                oldMessagesLength.current = messages.length;
            }
        };

        checkIfAllMessagesLoaded();
    }, [messages, conversation]);

    const handleScroll = async (event: UIEvent<HTMLDivElement>) => {
        const element = event.target as HTMLDivElement;
        const hasReachedTop =
            element.scrollHeight + element.scrollTop - MESSAGES_SPINNER_BOX_HEIGHT <= element.clientHeight;

        if (!hasReachedTop) {
            return;
        }

        // 先加载当前会话更早的消息
        if (conversation && messages && !hasLoadedAllMessages && !isLoadingMessages.current) {
            isLoadingMessages.current = true;
            oldMessagesLength.current = messages.length;
            const totalMessagesCount = await conversation?.getMessagesCount();

            if (totalMessagesCount && messages.length < totalMessagesCount) {
                dispatch(getMoreMessages({ anchor: totalMessagesCount - messages.length - 1, conversation }));
            }
            return;
        }

        // 当前会话已加载完，继续上滑加载跨会话历史
        if (
            hasLoadedAllMessages &&
            hasMoreHistory &&
            !isLoadingHistoryRef.current &&
            historyBefore
        ) {
            loadHistoryPage({ before: historyBefore, prepend: true });
        }
    };

    const renderSeparatorIfApplicable = (message: Message, i: number) => {
        const belongsToCurrentUser = message.author === conversationsClient?.user.identity;
        const isFirstUnreadMessage = message.index === (conversation?.lastReadMessageIndex as number) + 1;

        /*
         * Render date separator above the first message which is the first of a certain date
         * (the first message of any chunk cannot compare itself with previous,
         * and the i = 0 date separator is rendered before "Chat started" section)
         */
        if (i > 0 && isFirstOfDateGroup(message, i, visibleMessages as Message[])) {
            return <MessageListSeparator message={message} separatorType="date" />;
        }

        /*
         * Render New separator above the first unread message
         * (messages sent by the current user should be treated as inherently read to avoid flicker)
         */
        if (isFirstUnreadMessage && !belongsToCurrentUser) {
            return <MessageListSeparator message={message} separatorType="new" />;
        }

        return null;
    };

    const renderChatStarted = () =>
        hasLoadedAllMessages ? (
            <>
                <Box {...conversationEventContainerStyles}>
                    <Text as="h3" {...conversationEventTitleStyles} data-test="chat-started">
                        Chat started
                    </Text>
                    <Text as="p" {...conversationEventDateStyles}>
                        {conversation?.dateCreated.toLocaleString()}
                    </Text>
                </Box>
            </>
        ) : null;

    const renderPastHistory = () => (
        <>
            {(isLoadingHistory || (hasLoadedAllMessages && hasMoreHistory)) && (
                <Box {...spinnerContainerStyles}>
                    <Spinner color="colorTextWeak" decorative={false} title="Loading history" />
                </Box>
            )}

            {pastMessages
                .filter((message) => !isSystemStartMessage(message))
                .map((message) => (
                    <HistoryMessageBubble key={message.sid} message={message} customerEmail={email} />
                ))}

            {pastMessages.length > 0 ? (
                <Box {...conversationEventContainerStyles}>
                    <Text as="h3" {...conversationEventTitleStyles}>
                        以上为历史会话
                    </Text>
                    <Text as="p" {...conversationEventDateStyles}>
                        向上滑动可加载更早记录
                    </Text>
                </Box>
            ) : null}
        </>
    );

    const renderChatItems = () => {
        if (!messages) {
            return null;
        }

        /*
         * We use a copy of the messages array where the first message is a placeholder for the loading spinner.
         * By assigning the loading spinner to the same index and key as the previous message,
         * we avoid a snappy scroll position change when loading spinner disappears.
         */
        const spinnerIndex = (visibleMessages[0]?.index || messages[0]?.index || 0) - 1;
        const messagesWithSpinner = [
            {
                index: spinnerIndex
            } as Message,
            ...visibleMessages
        ];

        return messagesWithSpinner.map((message: Message, i: number) => {
            // First message in array represents the loading spinner
            if (message.index === spinnerIndex) {
                // Only render loading spinner if there are remaining messages to load
                return hasLoadedAllMessages ? null : (
                    <Box {...spinnerContainerStyles} key={message.index}>
                        <Spinner color="colorTextWeak" decorative={false} title="Loading" />
                    </Box>
                );
            }
            // Discount loading spinner from indices
            i -= 1;

            return (
                <Box data-test="all-message-bubbles" key={message.index}>
                    {renderSeparatorIfApplicable(message, i)}
                    <MessageBubble
                        message={message}
                        isLast={i === visibleMessages.length - 1}
                        isLastOfUserGroup={isLastOfUserGroup(message, i, visibleMessages)}
                        focusable={message.index === focusIndex}
                        updateFocus={updateFocus}
                    />
                </Box>
            );
        });
    };

    const handleFocus = () => {
        // Hand over focus to message bubbles once there is at least one
        if (messages && messages.length && focusIndex < 0) {
            setFocusIndex(messages[messages.length - 1].index);
        }
    };

    return (
        <Box {...messageListStyles}>
            <Box {...outerContainerStyles} onScroll={throttle(handleScroll, 1000)} ref={messageListRef} role="main">
                <Box
                    aria-label="Chat messages"
                    role="log"
                    aria-relevant="additions"
                    {...innerContainerStyles}
                    tabIndex={focusIndex >= 0 ? -1 : 0}
                    onFocus={handleFocus}
                >
                    {renderPastHistory()}
                    {renderChatStarted()}
                    {renderChatItems()}
                    {participants
                        ?.filter((p) => p.isTyping && p.identity !== conversationsClient?.user.identity)
                        .map((p) => (
                            <Text {...participantTypingStyles} as="p" key={p.identity}>
                                {users?.find((u) => u.identity === p.identity)?.friendlyName} is typing...
                            </Text>
                        ))}
                </Box>
            </Box>
        </Box>
    );
};
